
'use client';

import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';

export default function OfferSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [quantity, setQuantity] = useState(1);

  const productUrl = 'https://www.lavyre.com/cart';
  const displayPrice = 49.90;
  const displayComparePrice = 149.99;
  const savings = Number(
    ((displayComparePrice - displayPrice) / displayComparePrice) * 100
  ).toFixed(0);

  const handleBuyNow = () => {
    try {
      window.open(productUrl, '_blank');
    } catch (error) {
      console.error('Failed to open product URL:', error);
    }
  };

  // Safeguard quantity to always be a positive integer
  const safeQuantity = Math.max(1, Math.floor(quantity));

  return (
    <section
      id="offer"
      ref={ref}
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative h-[240px] sm:h-[320px] md:h-[400px] lg:h-[600px] overflow-hidden rounded-lg"
          >
            <img
              src="https://www.lavyre.com/cdn/shop/files/S772751a32b6642b3b38b819a4802371d9.webp?v=1770609778"
              alt="EmberLock™ Premium Pistol Lighter"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Offer Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white mb-3 sm:mb-4 md:mb-6"
            >
              EmberLock™ Premium Pistol Lighter
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-4 sm:mb-6 md:mb-8"
            >
              <div className="flex flex-wrap items-baseline gap-2 sm:gap-3 mb-1 sm:mb-2">
                <span className="text-white/50 text-base sm:text-lg md:text-xl lg:text-2xl line-through">
                  ${displayComparePrice.toFixed(2)}
                </span>
                <span className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif">
                  ${displayPrice.toFixed(2)}
                </span>
                <span className="bg-red-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs md:text-sm font-medium">
                  Save {savings}%
                </span>
              </div>
              <p className="text-white/70 text-[10px] sm:text-xs md:text-sm">
                Less than the cost of a night out.
              </p>
            </motion.div>

            {/* Quantity Selector */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-4 sm:mb-6 md:mb-8"
            >
              <label className="text-white/80 text-[10px] sm:text-xs md:text-sm mb-2 block">
                Quantity
              </label>
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 sm:w-12 sm:h-12 border border-white/30 rounded-lg text-white hover:bg-white/10 transition-all cursor-pointer text-sm sm:text-base active:scale-95"
                >
                  &minus;
                </button>
                <span className="text-white text-base sm:text-lg md:text-xl font-medium w-8 sm:w-12 text-center">
                  {safeQuantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-10 h-10 sm:w-12 sm:h-12 border border-white/30 rounded-lg text-white hover:bg-white/10 transition-all cursor-pointer text-sm sm:text-base active:scale-95"
                >
                  +
                </button>
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBuyNow}
              className="w-full bg-white text-black py-3 sm:py-4 rounded-full text-sm sm:text-base md:text-lg font-medium hover:bg-white/90 transition-all mb-2 sm:mb-3 md:mb-4 whitespace-nowrap cursor-pointer active:scale-[0.98]"
            >
              Buy Now — ${(displayPrice * safeQuantity).toFixed(2)}
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="text-white/60 text-[10px] sm:text-xs md:text-sm text-center mb-4 sm:mb-6 md:mb-8"
            >
              Or pay in 4 interest‑free installments
            </motion.p>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4"
            >
              <div className="flex items-center gap-2 text-white/80 text-[10px] sm:text-xs md:text-sm">
                <i className="ri-shield-check-line text-base sm:text-lg md:text-xl lg:text-2xl flex-shrink-0" />
                <span>7-Day Guarantee</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-[10px] sm:text-xs md:text-sm">
                <i className="ri-truck-line text-base sm:text-lg md:text-xl lg:text-2xl flex-shrink-0" />
                <span>Fast Shipping</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-[10px] sm:text-xs md:text-sm">
                <i className="ri-lock-line text-base sm:text-lg md:text-xl lg:text-2xl flex-shrink-0" />
                <span>Secure Checkout</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-[10px] sm:text-xs md:text-sm">
                <i className="ri-customer-service-line text-base sm:text-lg md:text-xl lg:text-2xl flex-shrink-0" />
                <span>24/7 Support</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
